import React, { useEffect, useState } from 'react';
import { useCandidateT } from '../../i18n/candidate';

/**
 * Full-screen "reading your resume" wait screen: document scan animation + cycling status line.
 * `title` and `messages` are English text; they are translated here for non-English candidate journeys.
 */
export default function ResumeProcessingOverlay({ title, messages, note, children }) {
  const t = useCandidateT();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % messages.length), 4000);
    return () => clearInterval(id);
  }, [messages.length]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-white/90 backdrop-blur-md transition-all duration-500"
      role="status"
    >
      <div className="flex flex-col items-center max-w-md w-full px-6 text-center animate-fade-in">
        <style>
          {`
            @keyframes scan-vertical {
              0%, 100% { top: 5%; }
              50% { top: 95%; }
            }
          `}
        </style>

        <div className="relative h-28 w-24 mb-8 flex-shrink-0 bg-gray-100 rounded-2xl overflow-hidden shadow-sm border border-gray-200 flex flex-col pt-6 px-4 gap-3">
          <div className="h-2 w-10 bg-electric-blue rounded-full" />
          <div className="flex gap-2">
            <div className="h-2 w-full bg-electric-blue rounded-full" />
            <div className="h-2 w-6 bg-electric-blue rounded-full" />
          </div>
          <div className="flex gap-2">
            <div className="h-2 w-8 bg-electric-blue rounded-full" />
            <div className="h-2 w-full bg-electric-blue rounded-full" />
          </div>
          <div className="h-2 w-12 bg-electric-blue rounded-full" />
          <div
            className="absolute left-0 right-0 h-1 bg-electric-blue shadow-[0_0_12px_2px_rgba(0,123,255,0.8)]"
            style={{ animation: 'scan-vertical 2.5s ease-in-out infinite' }}
          />
        </div>

        <h3 className="text-2xl font-extrabold text-gray-900 mb-3 tracking-tight">{t(title)}</h3>

        <div className="h-8 flex items-center justify-center w-full mb-6">
          <p className="text-base font-semibold text-electric-blue-600 transition-opacity duration-500" key={index}>
            {t(messages[index])}
          </p>
        </div>

        <div className="w-64 h-1.5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
          <div className="h-full bg-gradient-to-r from-electric-blue to-indigo-500 rounded-full w-full animate-pulse shadow-[0_0_10px_rgba(0,123,255,0.5)]" />
        </div>

        <p className="mt-8 text-sm font-medium text-gray-500">{note}</p>
        {children}
      </div>
    </div>
  );
}
